import { LuCheck, LuCircle, LuCrown, LuGift, LuLayers, LuMedal, LuShirt, LuSparkles, LuStar, LuTarget, LuTrophy, LuX } from "react-icons/lu";
import { useEffect, useMemo, useRef, useState } from "react";
import { get } from "../api";
import { Ring, calm } from "../components/fx";
import { ClaimButton, DEF_ICONS, GROUP_ICONS, HUES, MissionRow, NextCard, pickNext, starCount, useClaim } from "../components/goals";
import { FxLayer } from "../components/juice";
import { Coin, Coins, ErrorBox, Progress, Spinner } from "../components/ui";
import { HeroBubble, PageHero } from "../components/PageHero";
import { useLoad } from "../store";

/** the three little stars under a badge */
function Stars({ done, total }: { done: number; total: number }) {
  const n = starCount(done, total);
  return (
    <span className="stars" role="img" aria-label={`${n} af 3 stjerner`}>
      {[1, 2, 3].map((i) => <LuStar key={i} aria-hidden="true" size={15} className={i <= n ? "on" : ""} />)}
    </span>
  );
}

export function Achievements() {
  const ach = useLoad(() => get<{ groups: any[] }>("/api/achievements"), []);
  const mis = useLoad(() => get<{ missions: any[] }>("/api/missions"), []);
  const [open, setOpen] = useState<string | null>(null);
  const panel = useRef<HTMLElement | null>(null);
  const { claim, busy } = useClaim(() => { ach.reload(); mis.reload(); });
  const groups = ach.data?.groups ?? [];
  const totals = useMemo(() => ({ groups: groups.length, done: groups.filter((x) => x.completed).length, parts: groups.reduce((s, x) => s + x.total, 0), partsDone: groups.reduce((s, x) => s + x.done, 0) }), [groups]);
  const next = useMemo(() => pickNext(groups, 3), [groups]);
  /** v21: what is waiting to be collected – finished sub-goals, whole badges and missions */
  const waiting = useMemo(() => {
    const defs = groups.flatMap((g: any) => g.defs.filter((d: any) => d.claimable));
    const grp = groups.filter((g: any) => g.claimable);
    const ms = (mis.data?.missions ?? []).filter((m: any) => m.claimable);
    return { n: defs.length + grp.length + ms.length, coins: defs.reduce((s: number, d: any) => s + d.rewardCoins, 0) + grp.reduce((s: number, g: any) => s + g.reward.coins, 0) + ms.reduce((s: number, m: any) => s + m.reward.coins, 0) };
  }, [groups, mis.data]);
  useEffect(() => { if (open) panel.current?.scrollIntoView({ block: "nearest", behavior: calm() ? "auto" : "smooth" }); }, [open]);
  if (ach.loading && !ach.data) return <Spinner />;
  if (ach.error) return <ErrorBox error={ach.error} retry={ach.reload} />;
  const level = groups.filter((g) => g.kind === "level");
  const subject = groups.filter((g) => g.kind === "subject");
  const here = level.find((g) => !g.completed)?.id;
  const missions: any[] = mis.data?.missions ?? [];
  const daily = missions.filter((m) => m.period === "day");
  const weekly = missions.filter((m) => m.period === "week");
  const hue = (g: any) => HUES[Math.max(0, groups.indexOf(g)) % HUES.length];

  const tile = (g: any) => {
    const I = GROUP_ICONS[g.icon] ?? LuMedal;
    return (
      <button type="button" key={g.id} className={`badge-tile${g.completed ? " done" : ""}${here === g.id ? " here" : ""}${open === g.id ? " open" : ""}${g.claimable || g.claimableDefs > 0 ? " claimable" : ""}`} style={{ ["--h" as any]: hue(g) }} aria-expanded={open === g.id} aria-controls="bd-detail" onClick={() => setOpen(open === g.id ? null : g.id)} data-tilt="6">
        <Ring value={g.done} max={Math.max(1, g.total)} size={104} stroke={9} tone={g.completed ? "gold" : "blue"} label={`${g.name}: ${g.done} af ${g.total}`}>
          <span className="bt-ico"><I size={40} aria-hidden="true" /></span>
        </Ring>
        <b className="bt-name">{g.name}</b>
        <span className="bt-count">{g.completed ? <><LuCheck aria-hidden="true" size={15} /> Fuldført</> : <>{g.done} / {g.total}</>}</span>
        <Stars done={g.done} total={g.total} />
        {here === g.id && <span className="bt-here">Du er her</span>}
        {g.claimable ? <span className="bt-claim" aria-label="Badgen kan hentes"><LuGift aria-hidden="true" size={15} /> Hent badge!</span> : g.claimableDefs > 0 ? <span className="bt-claim sub" aria-label={`${g.claimableDefs} delmål kan hentes`}><LuGift aria-hidden="true" size={14} /> {g.claimableDefs} klar</span> : null}
      </button>
    );
  };
  const detail = (g: any) => {
    const I = GROUP_ICONS[g.icon] ?? LuMedal;
    return (
      <section id="bd-detail" ref={panel} className="badge-detail card" style={{ ["--h" as any]: hue(g) }} aria-label={`Delmål i ${g.name}`}>
        <div className="bd-head">
          <span className="bd-ico" aria-hidden="true"><I size={34} aria-hidden="true" /></span>
          <div className="bd-title">
            <h3>{g.name}</h3>
            <span className="bd-sub">{g.completed ? <><LuCheck aria-hidden="true" size={15} /> Fuldført</> : <>{g.done} af {g.total} · klar alle for præmien</>}</span>
          </div>
          <button type="button" className="btn ghost small bd-close" onClick={() => setOpen(null)} aria-label="Luk"><LuX aria-hidden="true" size={18} /></button>
        </div>
        <div className="bd-reward" aria-label="Gruppepræmie">
          <LuGift aria-hidden="true" size={20} />
          <Coins n={g.reward.coins} />
          {g.reward.title && <span className="chip"><LuCrown aria-hidden="true" size={15} /> {g.reward.title}</span>}
          {g.reward.cosmetic && <span className="chip"><LuShirt aria-hidden="true" size={15} /> {g.reward.cosmetic.name}</span>}
          {g.claimable && <ClaimButton label="Indløs badge" busy={!!busy} onClick={(el) => void claim("group", g.id, el)} />}
          {g.claimed && <ClaimButton done />}
        </div>
        <ul className="bd-list">
          {g.defs.map((d: any) => {
            const DI = DEF_ICONS[d.icon] ?? LuCircle;
            return (
              <li key={d.id} className={`subach${d.completed ? " ok" : ""}${d.claimable ? " claimable" : ""}`}>
                <span className="sa-ico" aria-hidden="true">{d.completed ? <LuCheck size={18} aria-hidden="true" /> : <DI size={18} aria-hidden="true" />}</span>
                <span className="sa-main">
                  <strong title={d.description}>{d.title}</strong>
                  <Progress value={d.progress} max={d.target} label={`${d.title}: ${d.progress} af ${d.target}`} tone="lime" />
                </span>
                <small className="sa-num">{d.completed ? "✓" : `${d.progress}/${d.target}`}</small>
                {d.claimable ? <span className="sa-claim"><ClaimButton coins={d.rewardCoins} busy={!!busy} onClick={(el) => void claim("def", d.id, el)} /></span>
                  : d.claimed ? <small className="sa-coin claimed-s"><LuCheck aria-hidden="true" size={13} /> hentet</small>
                  : <small className="sa-coin">+<Coin /> {d.rewardCoins}</small>}
              </li>
            );
          })}
        </ul>
      </section>
    );
  };
  const section = (title: string, list: any[], cls: string) => (
    <div className="stack">
      <h3 className="sec-sub">{title}</h3>
      <div className={`badge-grid ${cls}`} data-stagger>{list.map(tile)}</div>
      {open && list.some((g) => g.id === open) && detail(list.find((g) => g.id === open))}
    </div>
  );

  return (
    <div className="stack goals">
      <PageHero tone="goals" title="Mål" icon={<LuTarget size={44} />} sub="Små skridt – store badges">
        <div className="hbubble">
          <Ring value={totals.done} max={Math.max(1, totals.groups)} size={50} stroke={6} tone="gold" label="Badges"><b className="hb-frac">{totals.done}/{totals.groups}</b></Ring>
          <span><b>Badges</b><small>fuldført</small></span>
        </div>
        <HeroBubble icon={<LuStar aria-hidden="true" size={22} />} tone="blue" value={`${totals.partsDone}/${totals.parts}`} label="delmål klaret" />
      </PageHero>

      <FxLayer />
      {waiting.n > 0 && (
        <section className="claimbar" role="status" aria-label="Præmier klar til at hente">
          <span className="cb-ico" aria-hidden="true"><LuGift size={34} /></span>
          <span className="cb-text">
            <b>{waiting.n === 1 ? "1 præmie er klar!" : `${waiting.n} præmier er klar!`}</b>
            <small>Tryk for at hente dem – <Coins n={waiting.coins} /> venter på dig</small>
          </span>
          <button type="button" className="btn gold big cb-btn" disabled={!!busy} onClick={(e: any) => void claim("all", undefined, e.currentTarget as HTMLElement)}><LuSparkles aria-hidden="true" size={20} /> Indløs alt</button>
        </section>
      )}

      {next.length > 0 && (
        <section className="stack" aria-labelledby="g-next">
          <h2 id="g-next" className="sec-title"><LuTarget aria-hidden="true" /> Næste skridt</h2>
          <div className="next-grid" data-stagger>{next.map((c) => <NextCard key={c.def.id} def={c.def} hue={c.hue} />)}</div>
        </section>
      )}

      <section className="stack" aria-labelledby="g-mis">
        <h2 id="g-mis" className="sec-title"><LuTrophy aria-hidden="true" /> Missioner</h2>
        {mis.loading && <Spinner />}
        <ErrorBox error={mis.error} retry={mis.reload} />
        {missions.length > 0 && (
          <div className="mgrid">
            <div className="stack"><h3 className="sec-sub">I dag</h3>{daily.map((m) => <MissionRow key={m.key} m={m} busy={!!busy} onClaim={(el) => void claim("mission", m.key, el)} />)}</div>
            <div className="stack"><h3 className="sec-sub">Denne uge</h3>{weekly.map((m) => <MissionRow key={m.key} m={m} busy={!!busy} onClaim={(el) => void claim("mission", m.key, el)} />)}</div>
          </div>
        )}
      </section>

      <section className="stack" aria-labelledby="g-badges">
        <h2 id="g-badges" className="sec-title"><LuLayers aria-hidden="true" /> Badges</h2>
        {section("Niveau", level, "bg-path")}
        {section("Fag", subject, "bg-subj")}
      </section>
    </div>
  );
}
